/**
 * EmptyState — Pattern E from design-system/patterns.md.
 *
 * Centered placeholder with single ice-tinted icon, one-sentence description,
 * and one primary action. No mascots, no illustrations.
 */
import type { CSSProperties, ReactNode } from 'react';
import Button, { type ButtonVariant } from './Button';

export type EmptyStateSize = 'sm' | 'md' | 'lg';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: ButtonVariant;
}

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: EmptyStateAction;
  secondaryAction?: { label: string; onClick: () => void };
  size?: EmptyStateSize;
  /** Wrap in a hairline card surface — useful on otherwise empty pages. */
  framed?: boolean;
  style?: CSSProperties;
}

const sizes: Record<EmptyStateSize, { vPad: number; hPad: number; iconBox: number; titleSize: number }> = {
  sm: { vPad: 32, hPad: 16, iconBox: 36, titleSize: 14 },
  md: { vPad: 56, hPad: 24, iconBox: 44, titleSize: 15.5 },
  lg: { vPad: 80, hPad: 24, iconBox: 52, titleSize: 17 },
};

const defaultIcons: Record<string, ReactNode> = {
  search: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.6" />
      <path d="M16 16l5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  inbox: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M3 13h5l1 3h6l1-3h5M3 13l3-8h12l3 8M3 13v6a2 2 0 002 2h14a2 2 0 002-2v-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  folder: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M3 7v12a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-7l-2-2H5a2 2 0 00-2 2z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  document: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6zM14 3v6h6M9 13h6M9 17h6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  users: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <circle cx="9" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M2.5 20a6.5 6.5 0 0113 0M16 11a3 3 0 100-6M21.5 20a5.5 5.5 0 00-6-5.45" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  server: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <rect x="3" y="4" width="18" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <rect x="3" y="14" width="18" height="6" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
      <path d="M7 7h.01M7 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  ),
  chart: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
};

function EmptyState({
  icon,
  title,
  description,
  action,
  secondaryAction,
  size = 'md',
  framed = false,
  style,
}: EmptyStateProps) {
  const s = sizes[size];
  return (
    <div
      style={{
        textAlign: 'center',
        padding: `${s.vPad}px ${s.hPad}px`,
        ...(framed
          ? {
              background: 'var(--surface, oklch(0.11 0.012 230))',
              border: '1px dashed var(--border-strong, oklch(0.24 0.010 230))',
              borderRadius: 'var(--r-3, 10px)',
            }
          : null),
        ...style,
      }}
    >
      {icon && (
        <div
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: s.iconBox,
            height: s.iconBox,
            borderRadius: 'var(--r-3, 10px)',
            background: 'var(--tint-ice-bg)',
            border: '1px solid var(--tint-ice-br)',
            color: 'var(--tint-ice-fg)',
            marginBottom: 14,
          }}
        >
          {icon}
        </div>
      )}
      <h3
        style={{
          margin: 0,
          fontFamily: 'var(--font-display)',
          fontWeight: 500,
          fontSize: s.titleSize,
          letterSpacing: '-0.015em',
          color: 'var(--fg, oklch(0.94 0.003 230))',
        }}
      >
        {title}
      </h3>
      {description && (
        <p
          style={{
            margin: '8px auto 0',
            fontSize: 13,
            color: 'var(--fg-muted, oklch(0.56 0.006 230))',
            lineHeight: 1.55,
            maxWidth: 480,
          }}
        >
          {description}
        </p>
      )}
      {(action || secondaryAction) && (
        <div style={{ marginTop: 20, display: 'inline-flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          {action && (
            <Button variant={action.variant ?? 'ice'} onClick={action.onClick}>
              {action.label}
            </Button>
          )}
          {secondaryAction && (
            <Button variant="ghost" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

EmptyState.Icons = defaultIcons;

export default EmptyState;
